import { Box, Grid, Theme, Typography, createStyles, makeStyles } from '@material-ui/core';
import { CommissionPlan } from '@spike/commission-model';
import { reduceResolution, wbp } from 'Theme';
import clsx from 'clsx';
import { Switch } from 'components/UI';
import { FunctionComponent, memo, useMemo } from 'react';
import { sortByCommissionCategoryOrder } from './commissionCategoryOrder';

interface ViewCommissionProps {
    onClose: () => void;
    commissionPlan: CommissionPlan;
    active: boolean;
    onToggle?: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            flex: 1,
            height: '100%',
            flexWrap: 'nowrap',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            minHeight: 0,
            overflow: 'hidden'
        },
        header: {
            flexShrink: 0,
            width: '100% !important',
            padding: '16px 16px 32px',

            [theme.breakpoints.up('md')]: {
                padding: '37px 40px 16px 40px'
            }
        },
        close: {
            'cursor': 'pointer',
            '& button': {
                all: 'unset',
                padding: '6px 6px 0px',
                fontSize: 20
            }
        },
        body: {
            width: '100%',
            padding: '0px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '10px',

            [theme.breakpoints.up(wbp)]: {
                padding: '0px 40px 38px 40px'
            }
        },
        title: {
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            marginBottom: '45px'
        },
        titleRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%'
        },
        titleRight: {
            display: 'flex',
            alignItems: 'center',
            gap: 8
        },
        badge: {
            display: 'inline-flex',
            alignSelf: 'flex-start',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 50,
            padding: '4px 14px',
            fontSize: 13,
            fontWeight: 500,
            color: '#fff',
            lineHeight: '18px',
            height: '27px',
            minWidth: '69px'
        },
        badgeActive: {
            backgroundColor: '#5E8677'
        },
        badgeDeactivated: {
            backgroundColor: '#B5B5B5'
        },
        titleFont: {
            color: '#000',
            fontWeight: 600,
            marginTop: 0,

            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(25)}px`,
                lineHeight: `${reduceResolution(36)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '22px',
                lineHeight: '36px'
            }
        },
        dateContainer: {
            gap: '40px',
            display: 'flex',
            width: '100%',
            [theme.breakpoints.down('sm')]: {
                display: 'block'
            }
        },

        subtitle: {
            paddingBottom: '25px',
            borderBottom: '3px #000 solid',
            [theme.breakpoints.down('sm')]: {
                paddingBottom: '0px !important'
            }
        },
        subtitleFont: {
            color: '#000',
            fontWeight: 600,
            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(19)}px`,
                lineHeight: `${reduceResolution(30)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '16',
                lineHeight: '18px'
            },
            [theme.breakpoints.down('sm')]: {
                fontSize: '14',
                marginTop: 30
            }
        },
        subtitleFontActive: {
            [theme.breakpoints.down('sm')]: {
                marginBottom: 25
            }
        },
        dateFont: {
            color: '#000',
            fontWeight: 500,
            marginBottom: 10,
            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(19)}px`,
                lineHeight: `${reduceResolution(20)}px`,
                marginBottom: `${reduceResolution(25)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '15',
                lineHeight: '18px'
            },
            [theme.breakpoints.down('sm')]: {
                marginBottom: 35
            }
        },
        commissionType: {
            color: '#000',
            fontWeight: 500,
            marginTop: 15,
            marginBottom: 15,
            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(18)}px`,
                lineHeight: `${reduceResolution(15)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '15',
                lineHeight: '15px'
            }
        },
        date: {
            color: '#000',
            fontWeight: 500,
            marginBottom: 20,
            [theme.breakpoints.down(wbp)]: {
                fontSize: `${reduceResolution(19)}px`,
                lineHeight: `${reduceResolution(15)}px`,
                marginBottom: `${reduceResolution(40)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                fontSize: '15',
                lineHeight: '15px'
            }
        },
        rowSpacing: {
            [theme.breakpoints.down(wbp)]: {
                marginTop: `${reduceResolution(15)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                marginTop: '15px'
            }
        },
        footer: {
            padding: '16px',
            borderTop: '1px solid #DDDDDD',
            flexShrink: 0,
            width: '100%'
        },
        button: {
            width: '100%',
            height: 54,
            border: 'none',
            outline: 'none',
            cursor: 'pointer',
            fontSize: 18,
            fontWeight: 600,
            color: '#fff',
            backgroundColor: '#000',
            borderRadius: 30
        },
        commisionListContainer: {
            flex: 1,
            width: '100% !important',
            overflowY: 'auto',
            minHeight: 0,
            padding: '0px 16px',

            [theme.breakpoints.up('md')]: {
                padding: '0px 40px'
            }
        },
        commissionListItemContainer: {
            width: '100% !important',
            display: 'flex',
            alignItems: 'center',
            paddingTop: '20px',
            paddingBottom: '20px',
            marginTop: '5px'
        },
        commissionListItemNotLast: {
            borderBottom: '1px #D4D4D4 solid'
        },
        commissionListItemHeaderContainer: {
            width: '100%',
            display: 'flex'
        },
        commissionListItemHeader: {
            display: 'flex',
            width: '100%'
        },
        commissionListItemHeaderTitleContainer: {
            width: '90%'
        },

        commissionListItemHeaderValueContainer: {
            width: '10%'
        },

        itemCommisionTitle: {
            fontSize: '15px',
            fontWeight: 500
        },
        itemCommissionValue: {
            fontSize: '15px',
            fontWeight: 400
        }
    })
);

function ViewCommissionBase({ commissionPlan, active, onClose, onToggle }: ViewCommissionProps) {
    const classes = useStyles();

    const commissions = useMemo(
        () => sortByCommissionCategoryOrder(commissionPlan.commissions),
        [commissionPlan.commissions]
    );

    return (
        <Grid
            container
            className={classes.container}
        >
            <Box className={classes.header}>
                <Box className={classes.title}>
                    <Box className={classes.titleRow}>
                        <Typography className={classes.titleFont}>Commissions</Typography>
                        <Box className={classes.titleRight}>
                            {onToggle && (
                                <Switch
                                    checked={active}
                                    disabled={!active}
                                    onChange={onToggle}
                                />
                            )}
                        </Box>
                    </Box>
                    <Box
                        component="span"
                        className={clsx(classes.badge, active ? classes.badgeActive : classes.badgeDeactivated)}
                    >
                        {active ? 'Active' : 'Deactivated'}
                    </Box>
                </Box>
                <Box className={classes.subtitle}>
                    {active ? (
                        <Typography className={clsx(classes.subtitleFont, classes.subtitleFontActive)}>
                            From {commissionPlan.from.format('MMM DD, YYYY')} at {commissionPlan.from.format('hh:mm a')}
                        </Typography>
                    ) : (
                        <>
                            <Box className={classes.dateContainer}>
                                <Box>
                                    <Typography className={classes.subtitleFont}>Start Date</Typography>
                                    <Box>
                                        <Typography className={classes.dateFont}>
                                            {commissionPlan.from.format('MMM DD, YYYY')} at{' '}
                                            {commissionPlan.from.format('hh:mm a')}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography className={classes.subtitleFont}>End Date</Typography>
                                    <Typography className={classes.dateFont}>
                                        {commissionPlan.to && commissionPlan.to.format('MMM DD, YYYY')}
                                        {commissionPlan.to && ' at ' + commissionPlan.to.format('hh:mm a')}
                                    </Typography>
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
            </Box>
            <Box className={classes.commisionListContainer}>
                {commissions.map((commission, index) => {
                    return (
                        <Box
                            key={commission?.uuid}
                            className={
                                index + 1 === commissions.length
                                    ? classes.commissionListItemContainer
                                    : clsx(classes.commissionListItemContainer, classes.commissionListItemNotLast)
                            }
                        >
                            <Box className={classes.commissionListItemHeaderContainer}>
                                <Box className={classes.commissionListItemHeaderTitleContainer}>
                                    <Typography className={classes.itemCommisionTitle}>
                                        {commission.category.name}
                                    </Typography>
                                </Box>
                                <Box className={classes.commissionListItemHeaderValueContainer}>
                                    <Typography className={classes.itemCommissionValue}>
                                        {Number(commission.rate).toFixed(2)}%
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
            <Box className={classes.footer}>
                <button
                    type="button"
                    className={classes.button}
                    onClick={onClose}
                >
                    Done
                </button>
            </Box>
        </Grid>
    );
}

export const ViewCommission: FunctionComponent<ViewCommissionProps> = memo(ViewCommissionBase);

export default ViewCommission;
